import React, { useEffect, useState } from 'react';

import CourseAnalyticsAssessment from './CourseAnalyticsAssessment';
import CourseAnalyticsSurvey from './CourseAnalyticsSurvey';
import CourseAnalyticsTraineeProgress from './CourseAnalyticsTraineeProgress';
import CourseAnalyticsScorm from './CourseAnalyticsScorm';
import { Icon } from '@iconify/react';
import { coursePerformance } from '../../../services/adminService';
import { decodeData } from '../../../utils/encodeAndEncode';
import { useParams } from 'react-router-dom';

function CourseAnalytics() {
    const { courseId } = useParams();
    const decodedCourseId = decodeData(courseId);
    // console.log('Decoded Course ID', decodedCourseId);

    const [activeTab, setActiveTab] = useState('progress');
    const [courseStats, setCourseStats] = useState(null);
    const [loading, setLoading] = useState(true);

    // Fetch course performance data
    useEffect(() => {
        fetchCourseStats();
    }, [decodedCourseId]);

    const fetchCourseStats = async () => {
        try {
            setLoading(true);
            const response = await coursePerformance(decodedCourseId);
            // console.log('Course Stats Response:', response);

            if (response.success && response.data) {
                setCourseStats(response.data);
            }
        } catch (error) {
            console.error('Error fetching course stats:', error);
        } finally {
            setLoading(false);
        }
    };

    // Dynamic cards data based on API response
    const getCardsData = () => {
        if (!courseStats) {
            // Default/loading values
            return [
                {
                    title: 'Total Enrollments',
                    value: loading ? '...' : '0',
                    icon: 'fluent:people-24-regular',
                    bgColor: '#F4F7FE',
                    textColor: 'text-dark',
                    iconColor: 'text-primary'
                },
                // {
                //     title: 'Total Revenue',
                //     value: loading ? '...' : '$0',
                //     icon: 'fluent:money-24-regular',
                //     bgColor: '#E6FAF5',
                //     textColor: 'text-dark',
                //     iconColor: 'text-success'
                // },
                {
                    title: 'Completed',
                    value: loading ? '...' : '0',
                    icon: 'fluent:checkmark-circle-24-regular',
                    bgColor: '#E8F3FF',
                    textColor: 'text-dark',
                    iconColor: 'text-info'
                },
                {
                    title: 'In Progress',
                    value: loading ? '...' : '0',
                    icon: 'fluent:clock-24-regular',
                    bgColor: '#FFF6E5',
                    textColor: 'text-dark',
                    iconColor: 'text-warning'
                },
                {
                    title: 'Average Rating',
                    value: loading ? '...' : '0/5.0',
                    icon: 'fluent:star-24-regular',
                    bgColor: '#FFE7E7',
                    textColor: 'text-dark',
                    iconColor: 'text-danger'
                }
            ];
        }

        // Real data from API - using correct field names from old implementation
        const statsData = Array.isArray(courseStats) ? courseStats[0] : courseStats;

        return [
            {
                title: 'Total Enrollments',
                value: statsData?.total_purchased || 0,
                icon: 'fluent:people-24-regular',
                bgColor: '#F4F7FE',
                textColor: 'text-dark',
                iconColor: 'text-primary'
            },
            // {
            //     title: 'Total Revenue',
            //     value: `$${statsData?.total_revenue || 0}`,
            //     icon: 'fluent:money-24-regular',
            //     bgColor: '#E6FAF5',
            //     textColor: 'text-dark',
            //     iconColor: 'text-success'
            // },
            {
                title: 'Completed',
                value: statsData?.completed || 0,
                icon: 'fluent:checkmark-circle-24-regular',
                bgColor: '#E8F3FF',
                textColor: 'text-dark',
                iconColor: 'text-info'
            },
            {
                title: 'In Progress',
                value: statsData?.in_progress || 0,
                icon: 'fluent:clock-24-regular',
                bgColor: '#FFF6E5',
                textColor: 'text-dark',
                iconColor: 'text-warning'
            },
            {
                title: 'Average Rating',
                value: `${statsData?.average_rating || 0}/5.0`,
                icon: 'fluent:star-24-regular',
                bgColor: '#FFE7E7',
                textColor: 'text-dark',
                iconColor: 'text-danger'
            }
        ];
    };

    const cardsData = getCardsData();

    return (
        <>
            <div className="flex">
                <div className="w-full mb-4">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-100">
                        <div className="p-5">
                            <h4 className="text-xl font-semibold mb-4 text-gray-900">Course Analytics</h4>

                            {/* Analytics Cards */}
                            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
                                {cardsData.map((card, index) => (
                                    <div key={index}>
                                        <div className="rounded-lg border border-gray-100 bg-white shadow-sm">
                                            <div className="p-4">
                                                <div className="flex items-center mb-2">
                                                    <Icon
                                                        icon={card.icon}
                                                        width="24"
                                                        height="24"
                                                        className="mr-2 text-[#4F6BFF]"
                                                    />
                                                    <h6 className="text-sm font-medium mb-0 text-gray-700">
                                                        {card.title}
                                                    </h6>
                                                </div>
                                                <h2 className="text-2xl font-bold mb-0 text-gray-900">
                                                    {card.value}
                                                </h2>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Additional Sections */}
                            <div className="mt-4">
                                <div className="w-full">
                                    <div className="bg-white rounded-lg shadow-sm border border-gray-100">
                                        <div className="p-4">
                                            {/* Tabs Navigation */}
                                            <div className="border-b border-gray-100 mb-0">
                                                <ul className="flex flex-wrap border-b border-gray-200 px-2">
                                                    <li className="mr-2">
                                                        <button
                                                            className={`flex items-center px-4 py-2 border-b-2 ${activeTab === 'progress' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                                                            onClick={() => setActiveTab('progress')}
                                                        >
                                                            <Icon 
                                                                icon="fluent:people-community-24-regular" 
                                                                width="20" 
                                                                height="20" 
                                                                className="mr-2"
                                                            />
                                                            Trainee Progress
                                                        </button>
                                                    </li>
                                                    <li className="mr-2">
                                                        <button
                                                            className={`flex items-center px-4 py-2 border-b-2 ${activeTab === 'assessment' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                                                            onClick={() => setActiveTab('assessment')}
                                                        >
                                                            <Icon 
                                                                icon="fluent:clipboard-task-24-regular" 
                                                                width="20" 
                                                                height="20" 
                                                                className="mr-2"
                                                            />
                                                            Assessment
                                                        </button>
                                                    </li>
                                                    <li className="mr-2">
                                                        <button
                                                            className={`flex items-center px-4 py-2 border-b-2 ${activeTab === 'survey' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                                                            onClick={() => setActiveTab('survey')}
                                                        >
                                                            <Icon 
                                                                icon="fluent:form-24-regular" 
                                                                width="20" 
                                                                height="20" 
                                                                className="mr-2"
                                                            />
                                                            Survey Data
                                                        </button>
                                                    </li>
                                                    <li className="mr-2">
                                                        <button
                                                            className={`flex items-center px-4 py-2 border-b-2 ${activeTab === 'scorm' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                                                            onClick={() => setActiveTab('scorm')}
                                                        >
                                                            <Icon 
                                                                icon="fluent:archive-24-regular" 
                                                                width="20" 
                                                                height="20" 
                                                                className="mr-2"
                                                            />
                                                            SCORM Analytics
                                                        </button>
                                                    </li>
                                                </ul>
                                            </div>

                                            {/* Tab Content */}
                                            <div className="mt-4">
                                                <div className={`p-0 ${activeTab === 'progress' ? 'block' : 'hidden'}`}>
                                                    <div className="p-0 pt-2">
                                                        <CourseAnalyticsTraineeProgress />
                                                    </div>
                                                </div>

                                                <div className={`p-0 ${activeTab === 'assessment' ? 'block' : 'hidden'}`}>
                                                    <div className="p-0 pt-2">
                                                        <CourseAnalyticsAssessment />
                                                    </div>
                                                </div>

                                                <div className={`p-0 ${activeTab === 'survey' ? 'block' : 'hidden'}`}>
                                                    <div className="p-0 pt-2">
                                                        <CourseAnalyticsSurvey />
                                                    </div>
                                                </div>

                                                <div className={`p-0 ${activeTab === 'scorm' ? 'block' : 'hidden'}`}>
                                                    <div className="p-0 pt-2">
                                                        <CourseAnalyticsScorm />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default CourseAnalytics;
